import React, { useEffect, useRef, useState } from 'react';
import { X, ZoomIn } from 'lucide-react';
import { getGallery } from '../api/client';

const CATS = ['Semua', 'Hardware', 'Networking', 'Software', 'Lainnya'];

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState('Semua');
  const [lightbox, setLightbox] = useState(null);
  const ref = useRef();

  useEffect(() => {
    getGallery().then(setPhotos).catch(console.error);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { e.target.classList.add('visible'); obs.disconnect(); }
    }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Close lightbox on ESC
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (photos.length === 0) return null;

  const filtered = filter === 'Semua' ? photos : photos.filter(p => p.category === filter);

  return (
    <div style={{ padding: '120px 0', background: 'var(--bg)', overflow: 'hidden' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 32px' }}>
        <div ref={ref} className='reveal' style={{ marginBottom: 48, position: 'relative' }}>
          <div className='ghost-number' style={{ position: 'absolute', left: -20, top: -60, zIndex: 0 }}>GL</div>
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
            <div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#6a6a88', letterSpacing: 3, marginBottom: 12 }}>GALERI</div>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(36px,6vw,72px)', fontWeight: 700, color: '#f2f2ff', margin: 0, letterSpacing: '-2px', lineHeight: 1 }}>
                Dokumentasi<br />
                <span style={{ background: 'linear-gradient(135deg,#00e5ff,#9b00ff,#ff0099,#ffb700,#00e5ff)', backgroundSize: '400% 400%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'holo-shift 5s ease infinite' }}>Pekerjaan</span>
              </h2>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {CATS.filter(c => c === 'Semua' || photos.some(p => p.category === c)).map(c => (
                <button key={c} onClick={() => setFilter(c)}
                  style={{ padding: '7px 16px', borderRadius: 2, fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: 1, border: '1px solid', borderColor: filter === c ? '#00e5ff' : 'rgba(255,255,255,0.1)', background: filter === c ? 'rgba(0,229,255,0.08)' : 'transparent', color: filter === c ? '#00e5ff' : '#6a6a88', cursor: 'none', transition: 'all 0.2s' }}>
                  {c.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Masonry-style grid */}
        <div style={{ columns: 3, columnGap: 12 }} className='gallery-cols'>
          {filtered.map((photo, i) => (
            <div key={photo.id}
              onClick={() => setLightbox(photo)}
              style={{
                breakInside: 'avoid', marginBottom: 12,
                position: 'relative', overflow: 'hidden', borderRadius: 4,
                cursor: 'none', animation: `fadeIn 0.5s ease ${i*0.05}s both`,
              }}
            >
              <img loading="lazy" src={photo.image_url} alt={photo.title || ''}
                style={{ width: '100%', display: 'block', filter: 'brightness(0.8)', transition: 'filter 0.3s, transform 0.4s' }}
                onMouseEnter={e => { e.target.style.filter = 'brightness(1) saturate(1.1)'; e.target.style.transform = 'scale(1.03)'; }}
                onMouseLeave={e => { e.target.style.filter = 'brightness(0.8)'; e.target.style.transform = 'scale(1)'; }}
              />
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to top, rgba(7,7,15,0.7) 0%, transparent 50%)',
                display: 'flex', alignItems: 'flex-end', padding: 12,
                opacity: 0, transition: 'opacity 0.3s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0'}
              >
                <div style={{ flex: 1 }}>
                  {photo.title && <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: 13, color: '#f2f2ff' }}>{photo.title}</div>}
                  {photo.category && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#00e5ff', letterSpacing: 1 }}>{photo.category.toUpperCase()}</div>}
                </div>
                <ZoomIn size={18} color='rgba(255,255,255,0.7)' />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, cursor: 'none',
            animation: 'fadeIn 0.2s ease',
          }}
        >
          <button onClick={() => setLightbox(null)}
            style={{ position: 'absolute', top: 20, right: 20, width: 40, height: 40, borderRadius: 3, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: '#f2f2ff', cursor: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={18} />
          </button>
          <img src={lightbox.image_url} alt={lightbox.title || ''}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 4, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
          />
          {lightbox.title && (
            <div style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: 16, color: '#f2f2ff', marginBottom: 4 }}>{lightbox.title}</div>
              {lightbox.category && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#00e5ff', letterSpacing: 1 }}>{lightbox.category.toUpperCase()}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
